import React from "react";
import { Link } from "react-router-dom";
import { BiPlus } from "react-icons/bi";
import PricingSection from "./PricingSection";
import PlanComparison from "./PlanComparison";
import Footer from "../Footer/FooterPrice";
import StoreButtons from "../../components/StoreButtons";
import { webBillingEnabled } from "../../services/webBilling";

const quickFaqs = [
  {
    question: "Is VendorOrbis really free?",
    answer:
      "Yes. The Free plan has no time limit and needs no credit card. It includes booth checkout, Square, and event, inventory, and expense tracking for up to 10 active events and 25 items. You only pay when you need more, and Pro starts with a 30-day free trial for new subscribers: nothing to pay today, and you can cancel anytime.",
  },
  {
    question: "What counts toward the event limit?",
    answer:
      "Events you haven’t archived. When a show is over, archive it to make room for the next one.",
  },
  {
    question: "Where do I manage my subscription?",
    answer: webBillingEnabled
      ? "Subscribe here on the website or in the iPhone or Android app: either way your plan works everywhere. Manage a website subscription from Billing; App Store and Google Play subscriptions are managed in those stores."
      : "Subscriptions are managed in the mobile app. That is where you can subscribe, change plans, restore purchases, or review your current billing status.",
  },
  {
    question: "What happens when I upgrade?",
    answer:
      "Your existing data stays in place. Upgrading simply unlocks higher limits and features like more inventory capacity, more events, team tools, and advanced workflow options depending on the plan.",
  },
  {
    question: "Can I connect Square on the Free plan?",
    answer:
      "Yes. Square integration is included on every plan, including Free. Square processes card payments through the integrated mobile checkout, while VendorOrbis keeps sales tied to your event and inventory. No paid VendorOrbis subscription is required.",
  },
  {
    question: "Is my financial data secure?",
    answer:
      "Yes. Your sales, inventory, and finance data stay tied to your account, and sensitive payment setup like Square connection details is managed through secure authenticated flows.",
  },
  {
    question: "Can I write this off?",
    answer:
      "In many cases, software used to run your business can count as a business expense. Check with your accountant, but VendorOrbis may be tax-deductible depending on your situation.",
  },
];

const PricingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <PricingSection />
      <PlanComparison />

      <section className="bg-[#fcfcfa] px-4 py-20 md:px-8 md:py-28">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_1.6fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
              Questions, answered
            </h2>
            <p className="mt-4 text-slate-600 md:text-lg">
              What people usually ask before choosing a plan. Still curious?
              See the{" "}
              <Link to="/faq" className="font-medium text-blue-600 hover:underline">
                full FAQ
              </Link>{" "}
              or{" "}
              <Link
                to="/support"
                className="font-medium text-blue-600 hover:underline"
              >
                contact support
              </Link>
              .
            </p>
          </div>

          <div className="space-y-3">
            {quickFaqs.map((item) => (
              <details
                key={item.question}
                className="group rounded-2xl border border-slate-200 bg-white px-5 shadow-[0_10px_30px_-20px_rgba(15,23,42,0.25)] transition open:shadow-[0_20px_45px_-25px_rgba(15,23,42,0.3)] md:px-7"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left text-base font-semibold text-slate-900 md:text-lg [&::-webkit-details-marker]:hidden">
                  {item.question}
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition group-open:rotate-45 group-open:bg-blue-600 group-open:text-white">
                    <BiPlus size={18} />
                  </span>
                </summary>
                <p className="pb-6 text-base leading-7 text-slate-600">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#fcfcfa] px-4 pb-20 md:px-8 md:pb-28">
        <div className="relative isolate mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-[#070b17] px-6 py-14 text-center text-white md:px-12 md:py-20">
          <div
            aria-hidden
            className="absolute -bottom-40 left-1/2 -z-10 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/40 to-violet-600/40 blur-[110px]"
          />
          <h2 className="text-3xl font-semibold tracking-tight md:text-5xl">
            Your next market starts here
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-400 md:text-lg">
            Download VendorOrbis and set up your first event for free. When you
            outgrow the Free plan, try Pro free for 30 days.
          </p>
          <StoreButtons className="mt-9 justify-center" />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
